import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import _ from "lodash";

import theme from "theme";

import Button from "components/Button";
import Paper from "components/Paper";
import Stack from "components/Stack";
import { IHeader as ITableHeader } from "components/StyledTable";
import toast from "components/Toast";
import { useConfirmationDialog } from "components/useConfirmationDialog";
import { usePageTitle } from "components/usePageTitle";

import AssetManagementTabs from "entities/AssetManagement/components/AssetManagementTabs";
import PageHeader from "entities/AssetManagement/components/PageHeader";
import AssetManagementTable from "entities/AssetManagement/components/Table";
import Cell from "entities/AssetManagement/components/Table/components/Cell";
import YearCell from "entities/AssetManagement/components/Table/components/YearCell";
import { CELL_DEFAULT_WIDTH } from "entities/AssetManagement/components/Table/constants";
import {
  appendTotalRow,
  exportTableAsXLSX,
  insertYTDRows,
  nameColumn,
  transformApiHeaderToTableHeader,
  transformTableData,
} from "entities/AssetManagement/components/Table/utils";
import {
  AMColumn,
  AMResolution,
  AMVarianceDisplayMode,
} from "entities/AssetManagement/constants";
import { useAssetManagementExcelProcessingStatusUpdates } from "entities/AssetManagement/hooks";
import {
  IAssetManagementCategory,
  updateAssetManagement,
  useAssetManagementCategories,
  useAssetManagementData,
} from "entities/AssetManagement/sdk";
import {
  constructUpdatedDict,
  generateAllRowsFromNewAMDataStructure,
} from "entities/AssetManagement/utils";
import { useDealDetails } from "entities/Deal/sdk";

const TAB = "tab";

const AssetManagement = () => {
  const params = useParams<{
    dealId: string;
  }>();
  const dealId = Number(params.dealId);

  usePageTitle("Builders Patch: Asset Management");

  const { data: tabs } = useAssetManagementCategories({ dealId });

  const [resolution, setResolution] = useState<AMResolution>(
    AMResolution.YEARLY
  );
  const [showYearToDateColumn, setShowYearToDateColumn] =
    useState<boolean>(false);

  const [columns, setColumns] = useState<Array<AMColumn>>([
    AMColumn.BUDGETED,
    AMColumn.ACTUAL,
    AMColumn.AUDITED,
  ]);
  const [varianceDisplayMode, setVarianceDisplayMode] = useState<
    Array<AMVarianceDisplayMode>
  >([AMVarianceDisplayMode.ABSOLUTE, AMVarianceDisplayMode.PERCENTAGE]);

  const [selectedTab, setSelectedTab] =
    useState<IAssetManagementCategory | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [showSubItems, setShowSubItems] = useState<boolean>(true);
  const [hideEmptyRows, setHideEmptyRows] = useState<boolean>(false);
  const [changedCells, setChangedCells] = useState<{
    [key: string]: {
      current_value: number | null;
      previous_value: number | null;
    };
  }>({});
  const [deletedRowKeys, setDeletedRowKeys] = useState<Array<string>>([]);
  const [collapsedSubCategories, setCollapsedSubCategories] = useState<
    Array<string>
  >([]);
  const [searchParams, setSearchParams] = useSearchParams();

  const {
    data,
    isLoading: dataIsLoading,
    isValidating: dataIsValidating,
    mutate: updateAssetManagementData,
  } = useAssetManagementData({
    dealId,
  });

  const { data: deal } = useDealDetails({ dealId });

  const { show: showConfirmationDialog } = useConfirmationDialog();

  useEffect(() => {
    if (!editMode) {
      setChangedCells({});
      setDeletedRowKeys([]);
    }
  }, [editMode]);

  const handleChangeEditMode = useCallback(() => {
    if (editMode && !_.isEmpty(changedCells)) {
      showConfirmationDialog({
        onConfirm: () => {
          setEditMode(false);
        },
        confirmButtonText: "Discard",
        message: "Discard changes?",
      });
    } else {
      setEditMode((prev) => !prev);
    }
  }, [setEditMode, changedCells, showConfirmationDialog, editMode]);

  const handleCellChange = useCallback(
    ({
      itemKey,
      value,
      prevValue,
    }: {
      itemKey: string;
      value: number | null;
      prevValue: string | null;
    }) => {
      setChangedCells((prev) => ({
        ...prev,
        [itemKey]: {
          current_value: value,
          previous_value: prevValue ? parseFloat(prevValue || "") : null,
        },
      }));
    },
    [setChangedCells]
  );

  const allDataRows = useMemo(
    () => generateAllRowsFromNewAMDataStructure({ data, editMode }),
    [data, editMode]
  );

  const handleSave = useCallback(() => {
    if (_.isUndefined(allDataRows)) {
      return;
    }

    const calculatedDiff = constructUpdatedDict({
      tableData: allDataRows,
      changedCells,
      deletedRowKeys,
      yearMappings: {},
    });

    updateAssetManagement({
      dealId,
      diff: calculatedDiff,
    })
      .then(() => {
        setEditMode(false);
        setChangedCells({});
        updateAssetManagementData();
        toast.successMessage("Data saved successfully");
      })
      .catch((errors) => {
        const errorMessage = errors?.message || errors?.detail;

        if (!_.isNil(errorMessage)) {
          toast.errorMessage(errorMessage);
        } else {
          errors?.forEach?.((error: unknown) => {
            if (_.isString(error)) {
              toast.errorMessage(error);
            }
          });
        }
      });
  }, [
    allDataRows,
    changedCells,
    dealId,
    deletedRowKeys,
    updateAssetManagementData,
  ]);

  const changeTab = useCallback(
    (tab: IAssetManagementCategory) => {
      const urlParameters: URLSearchParams = new URLSearchParams();
      urlParameters.set(TAB, tab.name);
      setSearchParams(urlParameters);
      setSelectedTab(tab);
    },
    [setSearchParams]
  );

  const handleTabSelect = useCallback(
    (tab: IAssetManagementCategory) => {
      if (editMode && !_.isEmpty(changedCells)) {
        showConfirmationDialog({
          onConfirm: () => {
            setChangedCells({});
            changeTab(tab);
          },
          confirmButtonText: "Discard",
          message: "Discard changes?",
        });
      } else {
        changeTab(tab);
      }
    },
    [editMode, changedCells, changeTab, showConfirmationDialog]
  );

  useEffect(() => {
    if (_.isNil(tabs)) {
      return;
    }

    const tabInUrl = searchParams.get(TAB);

    if (tabInUrl && tabs.filter((tab) => tab.name === tabInUrl).length > 0) {
      setSelectedTab(
        tabs.find((tab) => tab.name === tabInUrl) as IAssetManagementCategory
      );
    } else {
      setSelectedTab(tabs[0]);
    }
  }, [tabs, searchParams]);

  const deleteRow = useCallback(({ rowKey }: { rowKey: string }) => {
    setDeletedRowKeys((prev) => [...prev, rowKey]);
  }, []);

  // If the data is not undefined and data.rows and data.headers are not empty, then we have a proforma
  const hasProforma = useMemo(
    () =>
      !_.isUndefined(data) &&
      !_.isEmpty(allDataRows.rows) &&
      !_.isEmpty(data.headers),
    [allDataRows.rows, data]
  );

  const handleSuccessUploadEvent = useCallback(() => {
    updateAssetManagementData();
    toast.successMessage("File processing has been completed successfully!");
  }, [updateAssetManagementData]);

  const handleFailedUploadEvent = useCallback(
    ({ detail }: { detail: string }) => toast.errorMessage(detail),
    []
  );

  const onCollapsedSubCategoriesChange = useCallback(
    ({ collapsedSubCategories }: { collapsedSubCategories: Array<string> }) => {
      setCollapsedSubCategories(collapsedSubCategories.map((item) => item));
    },
    []
  );

  useAssetManagementExcelProcessingStatusUpdates({
    successEventHandler: handleSuccessUploadEvent,
    failureEventHandler: handleFailedUploadEvent,
  });

  const dataFilteredByResolution = useMemo(
    () => ({
      headers: data?.headers || [],
      rows:
        allDataRows?.rows
          .map((row) => ({
            ...row,
            values: row.values
              .filter((value) => {
                if (resolution === AMResolution.MONTHLY) {
                  return !_.isNil(value.date_info.month);
                }

                if (resolution === AMResolution.QUARTERLY) {
                  return !_.isNil(value.date_info.quarter);
                }

                return (
                  _.isNil(value.date_info.month) &&
                  _.isNil(value.date_info.quarter)
                );
              })
              .filter((value) => columns.includes(value.data_type as AMColumn)),
          }))
          .filter((row) => (hideEmptyRows ? !_.isEmpty(row.values) : true)) ||
        [],
    }),
    [allDataRows, resolution, columns, hideEmptyRows, data]
  );

  useEffect(() => {
    if (showSubItems) {
      onCollapsedSubCategoriesChange({ collapsedSubCategories: [] });
    } else {
      onCollapsedSubCategoriesChange({
        collapsedSubCategories: dataFilteredByResolution.rows.map(
          (row) => row.sub_category.name
        ),
      });
    }
  }, [showSubItems, dataFilteredByResolution, onCollapsedSubCategoriesChange]);

  const columnsFilteredByResolution = useMemo(
    () =>
      columns.filter((column) =>
        resolution === AMResolution.MONTHLY ? column !== AMColumn.AUDITED : true
      ),
    [columns, resolution]
  );

  const headers: Array<
    ITableHeader<{
      label: string;
      itemCode: string;
      isTotalRow: boolean;
    }>
  > = useMemo(() => {
    if (_.isNil(dataFilteredByResolution) || _.isNil(selectedTab)) {
      return [];
    }

    let result = [
      nameColumn({
        selectedTab,
        setCollapsedSubCategories,
        collapsedSubCategories,
        editMode,
        handleRowDelete: deleteRow,
        deletedRowKeys,
      }),
      ...dataFilteredByResolution?.headers
        .filter((header) => {
          if (resolution === AMResolution.QUARTERLY) {
            return !_.isNull(header.quarter) && _.isNull(header.month);
          }

          if (resolution === AMResolution.MONTHLY) {
            return !_.isNull(header.month) && _.isNull(header.quarter);
          }

          return _.isNull(header.month) && _.isNull(header.quarter);
        })
        .map<
          ITableHeader<{
            isTotalRow: boolean | undefined;
            isAdded: boolean | undefined;
            id: number;
          }>
        >((header) =>
          transformApiHeaderToTableHeader({
            allSelectedHeaders: dataFilteredByResolution.headers,
            header,
            editMode,
            editHeaders: false,
            columns: columnsFilteredByResolution,
            onChange: handleCellChange,
            yearMappings: {},
            updateYearMapping: () => {},
            varianceDisplayMode,
          })
        ),
    ];

    if (showYearToDateColumn && !editMode) {
      const currentYear = new Date().getFullYear();

      const headerChildrenColumns = columns.filter((item) =>
        [AMColumn.BUDGETED, AMColumn.ACTUAL].includes(item as AMColumn)
      );

      if (headerChildrenColumns.length > 0) {
        // Insert YTD column as the first data column
        result = [
          result[0],
          {
            itemKey: `${currentYear}-YTD||`,
            label: `${currentYear} YTD`,
            width: headerChildrenColumns.length * CELL_DEFAULT_WIDTH + "px",
            renderHeader: ({ itemKey, label, Wrapper }) => (
              <YearCell
                label={label}
                itemKey={itemKey}
                Wrapper={(props) => (
                  <Wrapper
                    {...props}
                    style={{
                      ...props.style,
                      height: "49px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  />
                )}
                editMode={false}
                currentSelectedYears={[]}
                onChange={() => {}}
              />
            ),
            children: headerChildrenColumns.map((dataType) => ({
              itemKey: `${currentYear}-YTD|||${dataType}`,
              label: dataType,
              renderHeader: ({ label, Wrapper }) => (
                <Wrapper style={{ textAlign: "right" }}>{label}</Wrapper>
              ),
              render: ({ itemKey, row, index, Wrapper }) => (
                <Cell
                  itemKey={itemKey}
                  row={row as any}
                  index={index}
                  Wrapper={Wrapper}
                  editMode={editMode}
                  onChange={handleCellChange}
                />
              ),
            })),
          },
          ...result.slice(1),
        ];
      }
    }

    return result;
  }, [
    showYearToDateColumn,
    collapsedSubCategories,
    columns,
    editMode,
    resolution,
    selectedTab,
    deleteRow,
    deletedRowKeys,
    varianceDisplayMode,
    handleCellChange,
    columnsFilteredByResolution,
    dataFilteredByResolution,
  ]);

  const onExportButtonClick = useCallback(() => {
    if (
      _.isNil(dataFilteredByResolution) ||
      _.isNil(selectedTab) ||
      _.isNil(tabs) ||
      editMode
    ) {
      return;
    }
    let allTabsMapped = tabs.reduce(
      (acc, tab) => {
        acc[tab.name] = transformTableData({
          rows: dataFilteredByResolution.rows,
          selectedTab: tab,
          collapsedSubCategories: showSubItems
            ? []
            : dataFilteredByResolution.rows.map((row) => row.sub_category.name),
          changedCells,
          deletedRowKeys,
          columns: columnsFilteredByResolution,
          yearMappings: {},
          varianceDisplayMode,
        });

        return acc;
      },
      {} as Record<string, ReturnType<typeof transformTableData>>
    );
    if (showYearToDateColumn) {
      const currentYear = new Date().getFullYear();

      const dataTypes = columns.filter((item) =>
        [AMColumn.BUDGETED, AMColumn.ACTUAL].includes(item as AMColumn)
      );

      allTabsMapped = Object.fromEntries(
        Object.entries(allTabsMapped).map(([tabKey, tabRows]) => {
          let result = tabRows;

          result = insertYTDRows({
            rows: result,
            dataTypes,
            currentYear,
            sourceDataRows: !_.isNil(allDataRows?.rows)
              ? allDataRows?.rows
              : [],
          });

          return [tabKey, result];
        })
      ) as Record<string, ReturnType<typeof transformTableData>>;
    }

    exportTableAsXLSX({
      headers: headers as Array<{
        label: string;
        children: Array<{ label: string; itemKey: string }>;
        itemKey: string;
      }>,
      rows: allTabsMapped,
      dealName: deal?.name,
    });
  }, [
    tabs,
    headers,
    allDataRows?.rows,
    changedCells,
    deletedRowKeys,
    columns,
    varianceDisplayMode,
    showSubItems,
    showYearToDateColumn,
    columnsFilteredByResolution,
    deal?.name,
    dataFilteredByResolution,
    selectedTab,
    editMode,
  ]);

  const mappedTableData = useMemo(() => {
    if (_.isNil(dataFilteredByResolution) || _.isNil(selectedTab)) {
      return [];
    }

    let result = transformTableData({
      rows: dataFilteredByResolution.rows,
      selectedTab,
      collapsedSubCategories,
      changedCells,
      deletedRowKeys,
      columns: columnsFilteredByResolution,
      yearMappings: {},
      varianceDisplayMode,
      editMode,
    });

    if (showYearToDateColumn && !editMode) {
      const currentYear = new Date().getFullYear();

      const dataTypes = columns.filter((item) =>
        [AMColumn.BUDGETED, AMColumn.ACTUAL].includes(item as AMColumn)
      );

      result = insertYTDRows({
        rows: result,
        dataTypes,
        currentYear,
        sourceDataRows: !_.isNil(allDataRows?.rows) ? allDataRows?.rows : [],
      });
    }

    if (!editMode) {
      result = appendTotalRow({ rows: result, categoryName: selectedTab.name });
    }

    return result;
  }, [
    editMode,
    allDataRows?.rows,
    showYearToDateColumn,
    selectedTab,
    collapsedSubCategories,
    changedCells,
    deletedRowKeys,
    columnsFilteredByResolution,
    varianceDisplayMode,
    columns,
    dataFilteredByResolution,
  ]);

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: theme.spacing(0, 4, 1, 4),
      }}
    >
      <Stack spacing={3} direction="column">
        <PageHeader
          editMode={editMode}
          showSubItems={showSubItems}
          hideEmptyRows={hideEmptyRows}
          showYearToDateColumn={showYearToDateColumn}
          columns={columns}
          resolution={resolution}
          hasProforma={hasProforma}
          isLoading={dataIsLoading}
          onChangeEditMode={handleChangeEditMode}
          setShowYearToDateColumn={setShowYearToDateColumn}
          onChangeShowSubItems={setShowSubItems}
          onChangeHideEmptyRows={setHideEmptyRows}
          setColumns={setColumns}
          setResolution={setResolution}
          varianceDisplayMode={varianceDisplayMode}
          setVarianceDisplayMode={setVarianceDisplayMode}
          onExportButtonClick={onExportButtonClick}
        />
        {!_.isNil(selectedTab) && !_.isNil(tabs) && (
          <AssetManagementTabs
            tabs={tabs}
            selectedTab={selectedTab}
            onChange={handleTabSelect}
          />
        )}
      </Stack>
      {!_.isUndefined(dataFilteredByResolution) && !_.isNil(selectedTab) && (
        <AssetManagementTable
          resolution={resolution}
          isLoading={dataIsValidating}
          headers={headers}
          mappedTableData={mappedTableData}
          selectedTab={selectedTab}
        />
      )}
      {editMode && (
        <Button
          color="primary"
          onClick={handleSave}
          sx={{ marginTop: theme.spacing(2), alignSelf: "flex-end" }}
          data-testid="save-table-values-button"
        >
          Save
        </Button>
      )}
    </Paper>
  );
};

export default AssetManagement;
